/**
 * AdUnit — wraps a single Google AdSense <ins> element.
 *
 * Safety guarantees:
 * - The AdSense script is injected into <head> at most once per page load,
 *   regardless of how many AdUnit components mount or React route changes.
 * - adsbygoogle.push({}) is called exactly once per mounted <ins> element,
 *   guarded by a per-instance ref so React Strict Mode double-effects are safe.
 * - All ad failures are caught and silently discarded — repair content is
 *   never blocked by an ad error.
 */
import React, { useEffect, useRef } from 'react';

// Module-level singleton — survives React re-renders and route changes
let _scriptInjected = false;

function ensureAdSenseScript(publisherId: string): void {
  if (_scriptInjected) return;
  _scriptInjected = true;
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${publisherId}`;
  document.head.appendChild(s);
}

interface AdUnitProps {
  publisherId: string;
  slotId:      string;
  format?:     'auto' | 'fluid';
  className?:  string;
}

export default function AdUnit({
  publisherId,
  slotId,
  format = 'auto',
  className = '',
}: AdUnitProps) {
  const insRef  = useRef<HTMLInsElement>(null);
  const pushed  = useRef(false);

  useEffect(() => {
    if (!publisherId || !slotId) return;
    if (pushed.current) return;
    pushed.current = true;

    ensureAdSenseScript(publisherId);

    try {
      // adsbygoogle uses a command queue — safe to push before script loads
      (window as unknown as { adsbygoogle: unknown[] }).adsbygoogle =
        (window as unknown as { adsbygoogle: unknown[] }).adsbygoogle || [];
      (window as unknown as { adsbygoogle: unknown[] }).adsbygoogle.push({});
    } catch {
      // Silently fail — never let ad errors surface to visitors
    }
  }, [publisherId, slotId]);

  if (!publisherId || !slotId) return null;

  return (
    <ins
      ref={insRef}
      className={`adsbygoogle ${className}`}
      style={{ display: 'block' }}
      data-ad-client={publisherId}
      data-ad-slot={slotId}
      data-ad-format={format}
      data-full-width-responsive="true"
    />
  );
}

// ── Named placement wrappers ──────────────────────────────────────────────────

interface PlacementProps {
  publisherId:  string;
  slotId:       string;
  enabled:      boolean;
}

/** Top banner — sits below device title, above repair guides. */
export function AdBanner({ publisherId, slotId, enabled }: PlacementProps) {
  if (!enabled || !publisherId || !slotId) return null;
  return (
    <div className="my-6" aria-label="Advertisement">
      <p className="text-[10px] text-gray-700 uppercase tracking-widest text-center mb-1 select-none">
        Advertisement
      </p>
      <div className="min-h-[90px] overflow-hidden">
        <AdUnit publisherId={publisherId} slotId={slotId} format="auto" />
      </div>
    </div>
  );
}

/** In-content ad — sits between major sections. */
export function AdInContent({ publisherId, slotId, enabled }: PlacementProps) {
  if (!enabled || !publisherId || !slotId) return null;
  return (
    <div className="my-10" aria-label="Advertisement">
      <p className="text-[10px] text-gray-700 uppercase tracking-widest text-center mb-1 select-none">
        Advertisement
      </p>
      <div className="min-h-[250px] overflow-hidden">
        <AdUnit publisherId={publisherId} slotId={slotId} format="fluid" />
      </div>
    </div>
  );
}

/** Sidebar ad — currently reserved for future use. */
export function AdSidebar({ publisherId, slotId, enabled }: PlacementProps) {
  if (!enabled || !publisherId || !slotId) return null;
  return (
    <div aria-label="Advertisement">
      <p className="text-[10px] text-gray-700 uppercase tracking-widest text-center mb-1 select-none">
        Advertisement
      </p>
      <div className="min-h-[250px] overflow-hidden">
        <AdUnit publisherId={publisherId} slotId={slotId} format="auto" />
      </div>
    </div>
  );
}
